import { observable, makeObservable } from 'mobx'
import Rule from './Rule'
import RuleLogic from './RuleLogic'

class RuleGroup {
    ruleType = 'ruleGroup'

    @observable rules = []

    existingIds = new Set()

    constructor(defaultLogic = 'OR', rules = []) {
        makeObservable(this)
        this.defaultLogic = defaultLogic
        this.rules = rules
            .map((rule) => {
                const { ruleType } = rule

                switch (ruleType) {
                    case 'logic':
                        return rule instanceof RuleLogic ? rule : new RuleLogic(rule.logic)
                    case 'rule':
                        return rule instanceof Rule ? rule : new Rule(rule)
                    case 'ruleGroup':
                        return rule instanceof RuleGroup ? rule : new RuleGroup(rule.rules)
                    default:
                        return null
                }
            })
        // .filter((rule) => rule !== null)

        this.rules.forEach(({ ruleType, options }) => {
            if (ruleType === 'rule') {
                options.forEach(({ id }) => {
                    this.existingIds.add(id)
                })
            }
        })
    }

    discard = () => {
        this.existingIds = new Set()
        this.rules = []
    }

    static getDefaultLogic(type) {
        switch (type) {
            case 'campaign':
                return 'OR'
            default:
                return 'AND'
        }
    }

    static clone(ruleGroup) {
        console.log('clone', JSON.stringify(ruleGroup, null, 4))
        const rules = ruleGroup.rules.slice().map((rule) => {
            const { ruleType } = rule
            switch (ruleType) {
                case 'logic':
                    return RuleLogic.clone(rule)
                case 'rule':
                    return Rule.clone(rule)
                case 'ruleGroup':
                    return RuleGroup.clone(rule)
                default:
            }
        })
        const cloned = new RuleGroup(
            ruleGroup.defaultLogic,
            rules,
        )
        console.log('cloned', JSON.stringify(cloned, null, 4), rules)
        return cloned
    }

    addRule(rule, override = false) {
        const numberOfRules = this.rules.length
        const rules = this.rules.slice()

        const existingRule = rules.find(({ id }) => id === rule.id)

        if (existingRule && !override) {
            const options = rule.options.filter(({ id }) => !this.existingIds.has(id))
            if (options.length === 0) {
                console.log('existingRule', rule.options)
                return
            }
            existingRule.options.push(...options)
        } else if (existingRule && override) {
            rules.splice(0, 1, rule)
        } else if (numberOfRules > 0) {
            rules.push(new RuleLogic(this.defaultLogic), rule)
        } else {
            rules.push(rule)
        }

        rule.options.forEach(({ id }) => {
            this.existingIds.add(id)
        })

        this.rules = rules
    }

    removeRule(ruleId, optionId) {
        const rules = this.rules.slice()
        const existingRule = rules.find(({ id }) => id === ruleId)

        if (!existingRule) return

        const existingOptionIndex = existingRule.options.findIndex(({ id }) => id === optionId)

        if (existingOptionIndex === -1) return

        this.existingIds.delete(optionId)
        existingRule.options.splice(existingOptionIndex, 1)

        if (existingRule.options.length === 0) {
            const existingRuleIndex = rules.findIndex(({ id }) => id === ruleId)
            rules.splice(Math.max(0, (existingRuleIndex - 1)), 2)
        }

        this.rules = rules
    }

    hasRule = (ruleId) => this.existingIds.has(ruleId)

    hasRuleOption = (ruleId) => this.rules.some(({ id }) => ruleId === id)

    hasRules = () => this.existingIds.size > 0

    findRule = (ruleId) => this.rules.find(({ id }) => ruleId === id)

    toJSON() {
        return {
            ruleType: this.ruleType,
            rules: this.rules.slice().map((rule) => rule.toJSON()),
        }
    }
}

export default RuleGroup
